/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Element as ReactScrollElement } from 'react-scroll';

import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Button, Typography, Typography2 } from '@coursera/cds-core';

import { WaiverType } from 'bundles/degree-description/data/appFeeWaiverData';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { getAppFeeWaiverProps } from 'bundles/degree-description/utils/appFeeWaiver';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/degree-description';

const styles = {
  container: css`
    border: 1px solid var(--interactive-primary, #0056d2);
    border-radius: 8px;
    padding: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-200);
  `,
  marginBottom: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  step: css`
    margin-right: var(--cds-spacing-100);
  `,
  email: css`
    text-decoration: underline;
  `,
};

const CodeWaiver: React.FC<{ slug: string }> = ({ slug }) => {
  const { code, fee = '' } = getAppFeeWaiverProps(slug);

  return (
    <div>
      <Typography2 component="h3" variant="subtitleMedium" css={styles.marginBottom}>
        {_t('Limited time offer! Apply for free today!')}
      </Typography2>
      <Typography2 component="p" variant="bodyPrimary" css={styles.marginBottom}>
        {_t('Use the code #{code} for a free application', { code })}
      </Typography2>
      <div css={styles.marginBottom}>
        <Typography2 variant="subtitleMedium" component="span" css={styles.step}>
          {_t('Step 1:')}
        </Typography2>
        <Typography2 variant="bodyPrimary" component="span">
          {_t('Click "Apply now for free" above')}
        </Typography2>
      </div>
      <div>
        <Typography2 variant="subtitleMedium" component="span" css={styles.step}>
          {_t('Step 2:')}
        </Typography2>
        <Typography2 variant="bodyPrimary" component="span">
          {_t(
            'On the Authorization page of the application, enter the code #{code} (case sensitive and no spaces) in the discount code box to waive the #{fee} application fee',
            { code, fee }
          )}
        </Typography2>
      </div>
    </div>
  );
};

const LinkWaiver: React.FC<{ slug: string }> = ({ slug }) => {
  const { link, deadline = 'the deadline' } = getAppFeeWaiverProps(slug);
  const { trackClickButton } = useDegreeDescriptionEventingContext();

  const handleOnClick = () => {
    trackClickButton('degree_app_fee_waiver_link', 'page_header', link, _t('Redeem my free application now'));
  };

  return (
    <div>
      <Typography variant="h2semibold" css={styles.marginBottom}>
        {_t('Congratulations!')}
      </Typography>
      <Typography2 component="p" variant="bodyPrimary" css={styles.marginBottom}>
        {_t(
          'For a limited time, we are able to offer you a free application!  Use the link below and submit your application by #{deadline} to redeem this special, limited time offer.',
          { deadline: formatDateTimeDisplay(deadline, MOMENT_FORMATS.LONG_DATE_ONLY_NO_YEAR_DISPLAY) }
        )}
      </Typography2>
      <Button
        component={TrackedLink2}
        trackingName="app_fee_waiver_link"
        href={link}
        variant="secondary"
        onClick={handleOnClick}
      >
        {_t('Redeem my free application now')}
      </Button>
    </div>
  );
};

const EmailWaiver: React.FC<{ slug: string }> = ({ slug }) => {
  const { email, emailSubject } = getAppFeeWaiverProps(slug);

  return (
    <div>
      <Typography2 component="h3" variant="subtitleMedium" css={styles.marginBottom}>
        {_t('Limited time offer! Apply for free today!')}
      </Typography2>
      <Typography2 component="p" variant="bodyPrimary" css={styles.marginBottom}>
        {_t('To request a one-time application fee waiver:')}
      </Typography2>
      <div css={styles.marginBottom}>
        <Typography2 variant="subtitleMedium" component="span" css={styles.step}>
          {_t('Step 1:')}
        </Typography2>
        <Typography2 variant="bodyPrimary" component="span">
          {_t('Start your application by clicking "Apply now for free" above and select your program and term.')}
        </Typography2>
      </div>
      <div css={styles.marginBottom}>
        <Typography2 variant="subtitleMedium" component="span" css={styles.step}>
          {_t('Step 2:')}
        </Typography2>
        <Typography2 variant="bodyPrimary" component="span">
          {_t('Send an email to')}
        </Typography2>
        &nbsp;
        <Typography2 variant="bodyPrimary" component="a" href={`mailto:${email}`} css={styles.email}>
          {_t('#{email}', {
            email,
          })}
        </Typography2>
        &nbsp;
        <Typography2 variant="bodyPrimary" component="span">
          {_t('with the subject line "#{emailSubject}".', {
            emailSubject,
          })}
        </Typography2>
      </div>
      <div>
        <Typography2 variant="subtitleMedium" component="span" css={styles.step}>
          {_t('Step 3:')}
        </Typography2>
        <Typography2 variant="bodyPrimary" component="span">
          {_t('After receiving a confirmation email reply within 72 hours, you can submit your application for free!')}
        </Typography2>
      </div>
    </div>
  );
};

export const AppFeeWaiver: React.FC<{ slug: string }> = ({ slug }) => {
  const { type: waiverType } = getAppFeeWaiverProps(slug);

  return (
    <ReactScrollElement name="appFeeWaiver">
      <div css={styles.container}>
        {waiverType === WaiverType.CODE && <CodeWaiver slug={slug} />}
        {waiverType === WaiverType.LINK && <LinkWaiver slug={slug} />}
        {waiverType === WaiverType.EMAIL && <EmailWaiver slug={slug} />}
      </div>
    </ReactScrollElement>
  );
};
